import { createInstance } from '@optimizely/react-sdk'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import { get as getConfig } from '../../config/index'
import { getDeviceType } from '../../environment/index'
import { getAuthIsLoggedIn } from '../auth/index'
import { EN } from '../languages/constants'
import { getLocalStorage, setLocalStorage } from '../local-preferences/index'

const DEFAULT_BATCH_SIZE = 10
const DEFAULT_EVENT_FLUSH_INTERVAL = 1000
const LOCAL_STORAGE_USER_KEY = 'optimizely_feature_user_id'

const config = getConfig()
const optimizelySdkKey = _get(config, 'optimizelyFeatureSdkKey')

let optimizely = null

/**
 * Whether checkout currently has a plan selected in Redux.
 * @param {Object} state - The current application state.
 * @returns {boolean}
 */
export function getUserHasPlanSelected (state) {
  const { plans = Map() } = state
  const selectedPlan = plans.get('selection', Map()) || Map()
  return selectedPlan.size > 0
}

/**
 * Parses user_has_plan_selected from Redux or Optimizely user attributes.
 * @param {boolean|string|undefined} value - Stored attribute value
 * @returns {boolean}
 */
export function parseUserHasPlanSelectedAttribute (value) {
  return value === true || value === 'true'
}

/**
 * Formats user_has_plan_selected for Optimizely custom attribute (string type).
 * @param {boolean} hasPlan - Whether a plan is selected in checkout
 * @returns {'true'|'false'}
 */
export function formatUserHasPlanSelectedAttribute (hasPlan) {
  return hasPlan ? 'true' : 'false'
}

/**
 * Initializes and returns the Optimizely feature client.
 * This function checks if the client is already initialized to avoid multiple instances.
 * @returns {Object} - The Optimizely feature client instance.
 */
export function getOptimizelyFeatureClient () {
  const shouldInitialize = !optimizely

  if (shouldInitialize) {
    optimizely = createInstance({
      sdkKey: optimizelySdkKey,
      eventBatchSize: DEFAULT_BATCH_SIZE,
      eventFlushInterval: DEFAULT_EVENT_FLUSH_INTERVAL,
      // logLevel: 'debug',
    })
  }

  return optimizely
}

/**
 * Retrieves the Optimizely feature user object.
 * This function constructs a user object with various attributes based on the current state.
 * @param {Object} state - The current application state.
 * @returns {Object} - The Optimizely feature user object.
 */
export function getOptimizelyFeatureUser (state) {
  const {
    app,
    auth,
    user,
    resolver,
    userAccount,
    optimizelyFeature,
  } = state

  const id = getUserId(user, auth, optimizelyFeature)
  const resolverPath = resolver.get('path', null)
  const path = resolverPath || ((global && global.location) ? global.location.pathname : null) || '/'
  const device = getDeviceType()
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const subscriptions = auth.get('subscriptions') || List()
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''
  const currentPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
    || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'sku']) || ''
  const browserWidth = (global && global.innerWidth) ? global.innerWidth : 0
  const isTouchDevice = app.getIn(['viewport', 'touchable'], false)
  const userHasPlanSelected = getUserHasPlanSelected(state)

  const res = {
    id: id.toString(),
    attributes: {
      user_id: user.get('uid') || auth.get('uid') || -1,
      user_country: user.getIn(['data', 'requestCountry']) || auth.get('country') || '',
      user_device: device,
      user_language: user.getIn(['data', 'language', 0], EN),
      user_is_logged_in: isLoggedIn,
      user_active_flags: '',
      user_entitlements: subscriptions.join(', '),
      user_new_signup: false,
      user_time_hour: new Date().getHours(),
      user_time_minute: new Date().getMinutes(),
      user_plan_category: planCategory,
      user_current_plan_sku: currentPlanSku,
      user_has_plan_selected: formatUserHasPlanSelectedAttribute(userHasPlanSelected),
      user_browser_width: browserWidth,
      user_is_touch_device: isTouchDevice,
      page_url: (global && global.location) ? global.location.href : path,
      page_path: path,
      page_referrer: (global && global.document) ? global.document.referrer : '',
    },
  }
  return res
}

/**
 * Creates an Optimizely Feature event.
 * This function tracks an event with the Optimizely feature client.
 * @param {string} eventName - The name of the event to track.
 * @param {string} userId - The ID of the user to associate with the event.
 * @param {Object} tags - Additional tags to include with the event.
 */
export function setOptimizelyFeatureEvent (eventName, userId, tags) {
  const optimizelyClient = getOptimizelyFeatureClient()

  optimizelyClient.onReady().then(() => {
    optimizelyClient.track(eventName, userId, {}, { $opt_event_properties: tags })
  })
}

/**
 * Retrieves the user ID for Optimizely feature tracking.
 * This function checks local storage for a user ID, and if not found,
 * it generates a new anonymous UUID based on the user or auth state.
 * @param {Object} user - The user object from the application state.
 * @param {Object} auth - The auth object from the application state.
 * @returns {string} - The user ID to be used for Optimizely feature tracking.
 */
function getUserId (user = Map(), auth = Map(), optimizelyFeature = Map()) {
  const useAnonUid = optimizelyFeature.get('useAnonUid') || false
  const isLoggedIn = getAuthIsLoggedIn(auth) || false
  const uid = user.get('uid') || auth.get('uid') || null

  // Keep the same Optimizely user id for the session so experiment buckets stay sticky after login.
  const localStorageUser = getLocalStorage(LOCAL_STORAGE_USER_KEY)

  if (localStorageUser && localStorageUser.id) {
    return localStorageUser.id
  }

  if (isLoggedIn && uid && !useAnonUid) {
    return uid
  }

  const anonymousUuid = user.getIn(['data', 'anonymousUuid']) || auth.get('uuid') || ''
  const optimizelyUserId = anonymousUuid || createAnonymousOptimizelyUserId()
  setLocalStorage(LOCAL_STORAGE_USER_KEY, { id: optimizelyUserId }, uid, auth)

  return optimizelyUserId
}

/**
 * Fallback Optimizely user id when anonymous UUID is unavailable.
 * @returns {string}
 */
function createAnonymousOptimizelyUserId () {
  if (global && global.crypto && global.crypto.randomUUID) {
    return global.crypto.randomUUID()
  }

  return `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
